import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { AuthProvider } from '@/contexts/AuthContext';
import { TimerProvider } from '@/contexts/TimerContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ActiveWorkoutProvider } from '@/contexts/ActiveWorkoutContext';
import App from '@/App';
import '@/styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours (for offline support)
      refetchOnWindowFocus: false,
      retry: 1,
      networkMode: 'offlineFirst', // Use cache first when offline
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

// Persister for localStorage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'gymlab-cache',
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 1000 * 60 * 60 * 24, // 24 hours
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              // Only persist successful queries with data
              return query.state.status === 'success' && query.state.data !== undefined;
            },
          },
        }}
      >
        <AuthProvider>
          <TimerProvider>
            <ToastProvider>
              <ActiveWorkoutProvider>
                <App />
              </ActiveWorkoutProvider>
            </ToastProvider>
          </TimerProvider>
        </AuthProvider>
      </PersistQueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
